import { LeftOutlined, RightOutlined, LayoutOutlined, BuildOutlined, TeamOutlined } from '@ant-design/icons'
import { Button, Menu } from 'antd'
import { ItemType } from 'antd/es/menu/interface'
import clsx from 'clsx'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import styles from './main.sidebar.module.scss'
import logo from '../../../assets/images/logo.png'
import { RouteEnum } from '../../../constants/routes.enum'
import { useUserStore } from '../../../entities/user/stores/user.store'

import type { MenuProps } from 'antd'

type MenuItem = Required<MenuProps>['items'][number]

export const MainSidebar: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(true)
  const { user } = useUserStore()

  const childItems: Record<string, ItemType[]> = useMemo(() => {
    return {
      workspace: [
        {
          key: 'workspace',
          label: t('workspace.header'),
          disabled: false,
          onClick: () => navigate(`/workspace`),
        },
        {
          key: 'milestone',
          label: t('milestone.header'),
          disabled: !user?.isSuperUser && !user?.permissions.find((permission) => permission.includes('milestone_')),
          onClick: () => navigate(`/workspace/milestones`),
        },
        {
          key: 'milestone-sequence',
          label: t('milestoneSequence.header'),
          disabled: !user?.isSuperUser && !user?.permissions.find((permission) => permission.includes('milestone_')),
          onClick: () => navigate(`/workspace/milestones-sequence`),
        },
        {
          key: 'milestone-transfer',
          label: t('milestoneTransfer.header'),
          disabled: !user?.isSuperUser && !user?.permissions.find((permission) => permission.includes('milestonetransfer_')),
          onClick: () => navigate(`/workspace/milestones-transfer`),
        },
      ].filter((item) => !item.disabled),
      objects: [
        {
          key: 'objects',
          label: t('objectsPage.header'),
          disabled: !user?.isSuperUser && !user?.permissions.find((permission) => permission.includes('object_')),
          onClick: () => navigate(`/objects`),
        },
      ].filter((item) => !item.disabled),
      users: [
        {
          key: 'users',
          label: t('usersPage.header'),
          disabled: !user?.isSuperUser && !user?.permissions.find((permission) => permission.includes('user_')),
          onClick: () => navigate(`/users`),
        },
        {
          key: 'roles',
          label: t('usersPage.roles'),
          disabled: !user?.isSuperUser && !user?.permissions.find((permission) => permission.includes('role_')),
          onClick: () => navigate(`/roles`),
        },
      ].filter((item) => !item.disabled),
    }
  }, [user])

  const items: MenuItem[] = [
    {
      key: 'Workspace',
      label: t('workspace.header'),
      disabled: !childItems.workspace.length,
      icon: <LayoutOutlined />,
      children: childItems.workspace,
    },
    {
      key: 'Objects',
      label: t('objectsPage.header'),
      disabled: !childItems.objects.length,
      icon: <BuildOutlined />,
      children: childItems.objects,
    },
    {
      key: 'Users',
      label: t('usersPage.header'),
      icon: <TeamOutlined />,
      disabled: !childItems.users.length,
      children: childItems.users,
    },
  ]

  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
  }

  const handleLogoClick = () => {
    navigate(RouteEnum.MAIN)
  }

  return (
    <div className={clsx(styles.sidebar)}>
      <div className="logo cursor-pointer">
        <img
          alt="Sios-logo"
          onClick={handleLogoClick}
          src={logo}
        />
      </div>
      <Menu
        className={clsx(styles.sidebarMenu, {
          [styles['sidebarMenu--collapsed']]: collapsed,
        })}
        defaultOpenKeys={['sub1']}
        defaultSelectedKeys={['1']}
        inlineCollapsed={collapsed}
        inlineIndent={16}
        items={items}
        mode="inline"
        theme="dark"
      />
      <Button
        className={clsx(styles.sidebarCollapseButton)}
        onClick={toggleCollapsed}
        block
      >
        {collapsed ? <RightOutlined /> : <LeftOutlined />}
      </Button>
    </div>
  )
}
